/**
 * AdminUsersPage — `/users`. Управление командой (только admin).
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { Badge, Button, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { useAuth } from '@/auth/useAuth';
import { useAdminUsers, useAdminUserMutations, type AdminUser } from '@/hooks/api/useAdminUsers';

export function AdminUsersPage() {
  usePageHeader({ title: 'Пользователи', subtitle: 'Команда KOMMA Ads' });
  const { user: me } = useAuth();
  const { data, isLoading, isError, error, refetch } = useAdminUsers();
  const { create, patch, remove } = useAdminUserMutations();

  const [form, setForm] = useState({ username: '', password: '', display_name: '', role: 'user' as 'user' | 'admin' | 'seo' });

  if (isLoading) return <LoadingBlock label="Загружаю пользователей…" />;
  if (isError) return <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />;

  return (
    <div className="col gap20" style={{ maxWidth: 920 }}>
      <section className="card col" style={{ gap: 6 }}>
        <h3 style={{ margin: 0 }}>Доступы выдаются в КОММА IDM</h3>
        <span className="muted" style={{ fontSize: 13 }}>
          Учётки и роли сотрудников заводятся в{' '}
          <a href="https://idm.communitytech.group" target="_blank" rel="noreferrer">idm.communitytech.group</a>{' '}
          и приезжают сюда при входе через SSO. Локальные пользователи ниже — аварийный вход
          на случай недоступности SSO.
        </span>
      </section>

      <section className="card col" style={{ gap: 12 }}>
        <h3 style={{ margin: 0 }}>Добавить локального пользователя</h3>
        <div className="row wrap gap8" style={{ alignItems: 'flex-end' }}>
          <Field label="Логин" value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
          <Field label="Пароль" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <Field label="Имя" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
          <div className="col" style={{ gap: 4 }}>
            <label className="muted" style={{ fontSize: 12 }}>Роль</label>
            <select className="input" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'user' | 'admin' | 'seo' })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
              <option value="seo">seo</option>
            </select>
          </div>
          <Button variant="primary" size="sm" icon="Plus"
            disabled={!form.username || !form.password || !form.display_name || create.isPending}
            onClick={() =>
              create.mutate(form, { onSuccess: () => setForm({ username: '', password: '', display_name: '', role: 'user' }) })
            }>
            Создать
          </Button>
        </div>
        {create.isError && (
          <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>
            {(create.error as Error)?.message ?? 'Не удалось создать'}
          </span>
        )}
      </section>

      <section className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>Логин</th><th>Имя</th><th>Роль</th><th>Последний вход</th><th style={{ textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((u) => (
              <UserRow key={u.id} u={u} isMe={u.username === me?.username}
                onSetRole={(role) => patch.mutate({ id: u.id, role })}
                onSetPassword={() => {
                  const pw = window.prompt(`Новый пароль для «${u.username}»:`);
                  if (pw && pw.trim().length >= 4) patch.mutate({ id: u.id, password: pw.trim() });
                  else if (pw !== null) window.alert('Пароль — минимум 4 символа.');
                }}
                onDelete={() => remove.mutate(u.id)} />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function UserRow({ u, isMe, onSetRole, onSetPassword, onDelete }: {
  u: AdminUser; isMe: boolean; onSetRole: (role: 'user' | 'admin' | 'seo') => void; onSetPassword: () => void; onDelete: () => void;
}) {
  const roleTone = u.role === 'admin' ? 'b-violet' : u.role === 'seo' ? 'b-teal' : 'b-neutral';
  // Связанную с Keycloak учётку правит только IDM — локальные изменения затёрлись бы
  // при следующем входе, поэтому управление ролью и паролем здесь закрыто.
  const managedByIdm = Boolean(u.keycloak_sub);
  const idmHint = 'Управляется в КОММА IDM (idm.communitytech.group)';
  return (
    <tr>
      <td className="mono">
        {u.username}
        {isMe && <span className="muted"> · вы</span>}
        {managedByIdm && <> <span title={idmHint}><Badge tone="b-teal">SSO</Badge></span></>}
      </td>
      <td>{u.display_name}</td>
      <td><Badge tone={roleTone}>{u.role}</Badge></td>
      <td className="muted mono" style={{ fontSize: 12 }}>{u.last_login_at ? u.last_login_at.slice(0, 16).replace('T', ' ') : '—'}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            size="sm"
            icon="Key"
            onClick={onSetPassword}
            disabled={managedByIdm}
            title={managedByIdm ? idmHint : undefined}
          >
            Пароль
          </Button>
          <select
            className="input"
            value={u.role}
            disabled={isMe || managedByIdm}
            title={managedByIdm ? idmHint : undefined}
            onChange={(e) => onSetRole(e.target.value as 'user' | 'admin' | 'seo')}
            style={{ maxWidth: 100, fontSize: 12.5 }}
            aria-label="Роль"
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
            <option value="seo">seo</option>
          </select>
          <Button variant="danger-ghost" size="sm" icon="Trash" onClick={onDelete} disabled={isMe} aria-label="Удалить">
            Удалить
          </Button>
        </div>
      </td>
    </tr>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="col" style={{ gap: 4 }}>
      <label className="muted" style={{ fontSize: 12 }}>{label}</label>
      <input className="input" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
